import React, { useState } from 'react';
import {
  Calendar, Info, ArrowClockwise, Check, PencilSimple, X, Lightning,
} from '@phosphor-icons/react';
import {
  getBranchNames,
  getGroupCode,
  getChitGroupDetails,
  updateChitGroup,
  saveData,
} from '../api/connectionRegistryApi';
import { useLoginName } from '../utils/auditUser';
import SearchableSelect from './SearchableSelect';
import SectionLock from './SectionLock';
import { pickerOnly } from '../utils/pickerOnly';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

// Three independently editable fields, each with its own Edit toggle, so three
// change types. They used to share one TERMINATION_COMMENCE_DATE_CHANGE row: a
// PSO number edit was filed under a type named after dates, and Reports Of
// Updates could neither filter to it nor show it as its own change. The date
// pair keeps the original type because rows already exist under that name.
const SECTION_CHANGE_TYPES = {
  commencement: 'TERMINATION_COMMENCE_DATE_CHANGE',
  certificate: 'COMMENCEMENT_CERTIFICATE_NO_CHANGE',
  pso: 'PSO_NUMBER_CHANGE',
};

function toISODate(d) {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatDateForDisplay(d) {
  if (!d) return '—';
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

function ChangTermintndtCommencedt() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);
  const { toast, setToast, showToast } = useToast();

  const [branchlist, setBranchlist] = useState([]);
  const [groupList, setGroupList] = useState([]);

  const [cao, setCao] = useState('');
  const [group, setGroup] = useState('');

  const [branchschema, setBranchschema] = useState('');

  const [noActions, setNoActions] = useState('');
  const [certificateNo, setCertificateNo] = useState('');
  const [psoNo, setPsoNo] = useState('');
  const [commencementDt, setCommencementDt] = useState(null);
  const [terminationDt, setTerminationDt] = useState(null);

  const [newCommencementDt, setNewCommencementDt] = useState(null);
  const [newTerminationDt, setNewTerminationDt] = useState(null);
  const [newCertificateNo, setNewCertificateNo] = useState('');
  const [newPsoNo, setNewPsoNo] = useState('');

  const [minNewCommencementDate, setMinNewCommencementDate] = useState(null);
  const [maxNewCommencementDate, setMaxNewCommencementDate] = useState(null);

  const [editMode, setEditMode] = useState({ commencement: false, certificate: false, pso: false });

  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingUpdatePayload, setPendingUpdatePayload] = useState(null);
  // A list, not one entry: each edited section is audited under its own type.
  const [pendingAudit, setPendingAudit] = useState([]);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [pendingReason, setPendingReason] = useState('');

  React.useEffect(() => {
    getBranchNames().then((res) => setBranchlist(res || [])).catch(() => setBranchlist([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const resetDisplayFields = () => {
    setNoActions('');
    setCertificateNo('');
    setPsoNo('');
    setCommencementDt(null);
    setTerminationDt(null);
    setNewCommencementDt(null);
    setNewTerminationDt(null);
    setNewCertificateNo('');
    setNewPsoNo('');
    setMinNewCommencementDate(null);
    setMaxNewCommencementDate(null);
    setEditMode({ commencement: false, certificate: false, pso: false });
  };

  const resetFromBranch = () => {
    setBranchschema('');
    setGroupList([]);
    setGroup('');
    resetDisplayFields();
  };

  const loadGroups = async (schema) => {
    try {
      const res = await getGroupCode(schema);
      setGroupList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch (err) {
      console.error('Failed to load groups', err);
      setGroupList([]);
      showToast('error', 'Load Failed', 'Failed to load group codes. Please try again.');
    }
  };

  const handleCaoChange = (value) => {
    setCao(value || '');
    resetFromBranch();
    if (!value) return;
    const selected = branchlist.find((x) => x.branch_name === value);
    if (selected) {
      setBranchschema(selected.branch_code);
      loadGroups(selected.branch_code);
    }
  };

  const loadChitGroupDetails = async (schema, groupCodeValue) => {
    try {
      const res = await getChitGroupDetails(schema, groupCodeValue);
      if (!res || res.length === 0) {
        showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
        return;
      }
      const d = res[0];

      const commDate = d.commencement_date ? new Date(d.commencement_date) : null;
      const termDate = d.termination_date ? new Date(d.termination_date) : null;

      setNoActions(d.no_of_auctions ?? '');
      setCertificateNo(d.commencement_certificate_no ?? '');
      setPsoNo(d.pso_number ?? '');
      setCommencementDt(commDate);
      setTerminationDt(termDate);

      if (commDate) {
        setMinNewCommencementDate(commDate);
        const max = new Date(commDate);
        max.setMonth(max.getMonth() + 6);
        setMaxNewCommencementDate(max);
      } else {
        setMinNewCommencementDate(null);
        setMaxNewCommencementDate(null);
      }
    } catch (err) {
      console.error('Failed to load chit group details', err);
      showToast('error', 'Load Failed', 'Failed to load chit group details. Please try again.');
    }
  };

  const handleGroupChange = (value) => {
    setGroup(value || '');
    resetDisplayFields();
    if (!value) return;
    const selected = groupList.find((x) => x.group_Code === value);
    if (selected) loadChitGroupDetails(branchschema, selected.group_Code);
  };

  const recomputeTermination = (start) => {
    const months = Number(noActions);
    if (start && months) {
      const result = new Date(start);
      result.setMonth(result.getMonth() + months);
      result.setDate(result.getDate() - 1);
      setNewTerminationDt(result);
    } else {
      setNewTerminationDt(null);
    }
  };

  const handleNewCommencementChange = (dateStr) => {
    const selected = dateStr ? new Date(dateStr) : null;
    setNewCommencementDt(selected);
    recomputeTermination(selected);
  };

  const toggleEdit = (field) => {
    const nextValue = !editMode[field];
    setEditMode((prev) => ({ ...prev, [field]: nextValue }));

    if (field === 'commencement') {
      if (nextValue) {
        // Entering edit mode: copy Current Commencement Date to New Commencement Date
        const copied = commencementDt ? new Date(commencementDt) : null;
        setNewCommencementDt(copied);
        recomputeTermination(copied);
      } else {
        // Cancel
        setNewCommencementDt(null);
        setNewTerminationDt(null);
      }
    }

    if (field === 'pso' && !nextValue) {
      setNewPsoNo('');
    }

    if (field === 'certificate' && !nextValue) {
      setNewCertificateNo('');
    }
  };

  const caoInvalid = touched && !cao;
  const groupInvalid = touched && !group;
  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);
  const psoLengthInvalid = touched && newPsoNo.length > 20;
  const certificateLengthInvalid = touched && newCertificateNo.length > 20;

  const commencementError = (() => {
    if (!newCommencementDt || !minNewCommencementDate || !maxNewCommencementDate) return null;
    if (newCommencementDt < minNewCommencementDate) return 'beforeCommencement';
    if (newCommencementDt > maxNewCommencementDate) return 'afterSixMonths';
    return null;
  })();

  const handleSave = () => {
    setTouched(true);

    if (!cao || !group || (reason.trim().length < 10 || reason.length > 200)
      || (editMode.commencement && commencementError)
      || newPsoNo.length > 20 || newCertificateNo.length > 20) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    if (!editMode.commencement && !editMode.certificate && !editMode.pso) {
      showToast('warn', 'No Changes', 'Click the Edit button next to a field to modify it.');
      return;
    }

    // newData still goes to the API as one payload — the endpoint updates the
    // whole chit group in a single call — but the audit is split, one entry per
    // edited section, so each change type stands on its own in Reports.
    const oldData = {};
    const newData = {};
    const changes = [];
    const auditSections = [];

    if (editMode.commencement) {
      oldData.commencement_date = commencementDt;
      newData.commencement_date = newCommencementDt;
      oldData.termination_date = terminationDt;
      newData.termination_date = newTerminationDt;

      auditSections.push({
        section: 'commencement',
        oldData: { commencement_date: commencementDt, termination_date: terminationDt },
        newData: { commencement_date: newCommencementDt, termination_date: newTerminationDt },
      });

      changes.push({
        label: 'Commencement Date',
        oldValue: formatDateForDisplay(commencementDt),
        newValue: formatDateForDisplay(newCommencementDt),
      });
      changes.push({
        label: 'Termination Date',
        oldValue: formatDateForDisplay(terminationDt),
        newValue: formatDateForDisplay(newTerminationDt),
      });
    }

    if (editMode.certificate) {
      oldData.commencement_certificate_no = certificateNo;
      newData.commencement_certificate_no = newCertificateNo;

      auditSections.push({
        section: 'certificate',
        oldData: { commencement_certificate_no: certificateNo },
        newData: { commencement_certificate_no: newCertificateNo },
      });

      changes.push({
        label: 'Certificate No.',
        oldValue: certificateNo || '—',
        newValue: newCertificateNo || '—',
      });
    }

    if (editMode.pso) {
      oldData.pso_number = psoNo;
      newData.pso_number = newPsoNo;

      auditSections.push({
        section: 'pso',
        oldData: { pso_number: psoNo },
        newData: { pso_number: newPsoNo },
      });

      changes.push({
        label: 'PSO Number',
        oldValue: psoNo || '—',
        newValue: newPsoNo || '—',
      });
    }

    setPendingChanges(changes);
    setPendingReason(reason);
    setPendingUpdatePayload({ groupcode: group, ...newData });
    const loginTime = new Date().toISOString();
    setPendingAudit(auditSections.map((s) => ({
      schemaName: branchschema,
      loginName,
      loginTime,
      changeType: SECTION_CHANGE_TYPES[s.section],
      oldData: { groupCode: group, ...s.oldData },
      newData: { groupCode: group, ...s.newData },
      reason,
    })));

    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
    setPendingUpdatePayload(null);
    setPendingAudit([]);
    setPendingChanges([]);
  };

  const doSave = async () => {
    if (!pendingUpdatePayload || !pendingAudit?.length) return;

    setSaving(true);
    try {
      await updateChitGroup(branchschema, pendingUpdatePayload);
      try {
        // Already an array: one entry per edited section, one row each.
        await saveData(pendingAudit);
        setSaving(false);
        setShowConfirmModal(false);
        showToast('success', 'Success', 'Details updated and audit saved successfully.', true);
        handleClear();
      } catch (err) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('Audit save failed', err);
        showToast('warn', 'Partial Success', 'Record updated, but audit log failed to save.', true);
      }
    } catch (err) {
      setSaving(false);
      setShowConfirmModal(false);
      console.error('Update failed', err);
      showToast('error', 'Update Failed', 'Could not update the details. Please try again.', true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    resetFromBranch();
    setReason('');
    setTouched(false);
  };

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="tcd-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="tcd-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Calendar size={18} color="#002FA7" />
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Change Request</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Termination &amp; Commencement Date Change</div>
            </div>
          </div>
        </div>

        {/* SECTION A: Select Group */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Group</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch and group to load chit group details</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              CAO <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchlist}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_name}
              value={cao}
              onChange={handleCaoChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={caoInvalid}
              testId="tcd-cao-select"
            />
            {caoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>CAO is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Group Code <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`group-${formKey}`}
              options={groupList}
              getLabel={(o) => o.group_Code}
              getValue={(o) => o.group_Code}
              value={group}
              onChange={handleGroupChange}
              placeholder="Select group"
              emptyMessage="Select a CAO first"
              disabled={!cao}
              error={groupInvalid}
              testId="tcd-group-select"
            />
            {groupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Group Code is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>No. of Auctions</label>
            <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={noActions} readOnly data-testid="tcd-no-actions" />
          </div>
        </div>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!group} hint="Select a group in section A first">
        {/* SECTION B: Commencement & Termination Dates */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Commencement &amp; Termination Dates</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Click Edit to set a new commencement date — termination date is auto-computed</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>Current Commencement Date</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={formatDateForDisplay(commencementDt)} readOnly data-testid="tcd-old-commencement" />
              <button
                type="button"
                onClick={() => toggleEdit('commencement')}
                disabled={!group}
                className="btn-secondary"
                style={{ padding: '6px 12px', fontSize: '12px', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px' }}
                data-testid="tcd-commencement-edit"
              >
                {editMode.commencement ? <X size={12} /> : <PencilSimple size={12} />}
                {editMode.commencement ? 'Cancel' : 'Edit'}
              </button>
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>Current Termination Date</label>
            <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={formatDateForDisplay(terminationDt)} readOnly data-testid="tcd-old-termination" />
          </div>
        </div>

        {editMode.commencement && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginTop: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
                New Commencement Date <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <input
                type="date"
                className="input-field st-datepicker"
                style={{ fontSize: '13px', borderColor: commencementError ? '#DC2626' : undefined }}
                value={toISODate(newCommencementDt)}
                min={toISODate(minNewCommencementDate)}
                max={toISODate(maxNewCommencementDate)}
                onChange={(e) => handleNewCommencementChange(e.target.value)}
                {...pickerOnly()}
                data-testid="tcd-new-commencement"
              />
              {commencementError === 'beforeCommencement' && (
                <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Cannot be earlier than the current commencement date</div>
              )}
              {commencementError === 'afterSixMonths' && (
                <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Cannot be more than 6 months after the current commencement date</div>
              )}
            </div>

            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
                New Termination Date
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', fontSize: '10px', fontWeight: '700', color: '#002FA7', background: '#EFF4FF', padding: '1px 6px', borderRadius: '999px' }}>
                  <Lightning size={10} weight="fill" /> Auto
                </span>
              </label>
              <input
                className="input-field"
                style={{ fontSize: '13px', background: '#F9FAFB' }}
                value={newTerminationDt ? formatDateForDisplay(newTerminationDt) : ''}
                placeholder="Auto-filled after date selected"
                readOnly
                data-testid="tcd-new-termination"
              />
            </div>
          </div>
        )}
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!group} hint="Select a group in section A first">
        {/* SECTION C: PSO Number */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>PSO Number</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Click Edit to update the PSO number</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>PSO Number</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={psoNo} readOnly data-testid="tcd-old-pso" />
              <button
                type="button"
                onClick={() => toggleEdit('pso')}
                disabled={!group}
                className="btn-secondary"
                style={{ padding: '6px 12px', fontSize: '12px', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px' }}
                data-testid="tcd-pso-edit"
              >
                {editMode.pso ? <X size={12} /> : <PencilSimple size={12} />}
                {editMode.pso ? 'Cancel' : 'Edit'}
              </button>
            </div>
          </div>

          {editMode.pso && (
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>New PSO Number</label>
              <input
                className="input-field"
                style={{ fontSize: '13px', borderColor: psoLengthInvalid ? '#DC2626' : undefined }}
                maxLength={20}
                placeholder="Enter new PSO number"
                value={newPsoNo}
                onChange={(e) => setNewPsoNo(e.target.value)}
                data-testid="tcd-new-pso"
              />
              {psoLengthInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Max 20 characters allowed</div>}
            </div>
          )}
        </div>
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!group} hint="Select a group in section A first">
        {/* SECTION D: Certificate Number */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>D</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Commencement Certificate Number</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Click Edit to update the certificate number</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>Commencement Certificate No.</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={certificateNo} readOnly data-testid="tcd-old-certificate" />
              <button
                type="button"
                onClick={() => toggleEdit('certificate')}
                disabled={!group}
                className="btn-secondary"
                style={{ padding: '6px 12px', fontSize: '12px', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px' }}
                data-testid="tcd-certificate-edit"
              >
                {editMode.certificate ? <X size={12} /> : <PencilSimple size={12} />}
                {editMode.certificate ? 'Cancel' : 'Edit'}
              </button>
            </div>
          </div>

          {editMode.certificate && (
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>New Certificate No.</label>
              <input
                className="input-field"
                style={{ fontSize: '13px', borderColor: certificateLengthInvalid ? '#DC2626' : undefined }}
                maxLength={20}
                placeholder="Enter new certificate number"
                value={newCertificateNo}
                onChange={(e) => setNewCertificateNo(e.target.value)}
                data-testid="tcd-new-certificate"
              />
              {certificateLengthInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Max 20 characters allowed</div>}
            </div>
          )}
        </div>
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!group} hint="Select a group in section A first">
        {/* SECTION E: Reason */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>E</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason for Change</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
          <span style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
            {reason.length} / 200
          </span>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={4}
          maxLength={200}
          placeholder="Enter reason for this change..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="tcd-reason"
        />
        {reasonInvalid && (
          <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
            {reason.trim().length === 0 ? 'Reason is required' : reason.trim().length < 10 ? 'Minimum 10 characters required' : 'Maximum 200 characters allowed'}
          </div>
        )}
        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="tcd-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={handleSave} className="btn-primary" disabled={saving} data-testid="tcd-save-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '460px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="tcd-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Calendar size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Date/Details Change</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to update the following details for this chit group. Please review before confirming.
              </p>

              {pendingChanges.map((change) => (
                <div key={change.label} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>{change.label} (Old)</p>
                    <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{change.oldValue}</p>
                  </div>
                  <ArrowClockwise size={0} />
                  <span style={{ color: '#9CA3AF', fontSize: '13px' }}>→</span>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>{change.label} (New)</p>
                    <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>{change.newValue}</p>
                  </div>
                </div>
              ))}

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="tcd-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={doSave} disabled={saving} className="btn-primary" data-testid="tcd-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChangTermintndtCommencedt;